import { useMemo } from 'react';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { Text, ButtonLink, Button } from '@backstage/ui';
import { appTranslationRef } from '../../../core/i18n';
import styles from './kpc-not-found.module.css';

const stars = Array.from({ length: 20 }, (_, i) => ({
  id: i,
  left: `${Math.random() * 100}%`,
  top: `${Math.random() * 100}%`,
  delay: `${Math.random() * 4}s`,
  duration: `${2 + Math.random() * 3}s`,
  size: `${1 + Math.random() * 2}px`,
}));

const AstronautSvg = () => (
  <svg
    width="80"
    height="100"
    viewBox="0 0 80 100"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Helmet */}
    <ellipse cx="40" cy="28" rx="20" ry="22" fill="#3a3a5c" />
    <ellipse cx="40" cy="28" rx="20" ry="22" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" />
    {/* Visor */}
    <ellipse cx="40" cy="26" rx="14" ry="14" fill="#16162a" />
    <ellipse
      cx="40"
      cy="26"
      rx="14"
      ry="14"
      fill="url(#visorGradient)"
      opacity="0.5"
    />
    {/* Visor reflection */}
    <ellipse cx="34" cy="22" rx="4" ry="3" fill="rgba(255,255,255,0.12)" />
    {/* Body */}
    <rect x="22" y="48" width="36" height="30" rx="10" fill="#3a3a5c" />
    <rect
      x="22"
      y="48"
      width="36"
      height="30"
      rx="10"
      stroke="rgba(255,255,255,0.1)"
      strokeWidth="1"
    />
    {/* Chest panel */}
    <rect x="32" y="54" width="16" height="10" rx="3" fill="#232340" />
    <circle cx="37" cy="59" r="1.5" fill="#e8914f" opacity="0.8" />
    <circle cx="43" cy="59" r="1.5" fill="#5A3FD9" opacity="0.8" />
    {/* Left arm */}
    <rect x="10" y="52" width="14" height="8" rx="4" fill="#3a3a5c" transform="rotate(-15, 17, 56)" />
    {/* Right arm */}
    <rect x="56" y="50" width="14" height="8" rx="4" fill="#3a3a5c" transform="rotate(20, 63, 54)" />
    {/* Left leg */}
    <rect x="26" y="74" width="10" height="14" rx="5" fill="#3a3a5c" />
    {/* Right leg */}
    <rect x="44" y="74" width="10" height="14" rx="5" fill="#3a3a5c" />
    {/* Backpack */}
    <rect x="18" y="50" width="6" height="20" rx="3" fill="#2a2a48" />
    {/* Antenna */}
    <line x1="40" y1="6" x2="40" y2="2" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" />
    <circle cx="40" cy="1" r="2" fill="#e8914f" opacity="0.6" />
    <defs>
      <linearGradient id="visorGradient" x1="26" y1="14" x2="54" y2="38">
        <stop offset="0%" stopColor="#e8914f" stopOpacity="0.3" />
        <stop offset="100%" stopColor="#5A3FD9" stopOpacity="0.4" />
      </linearGradient>
    </defs>
  </svg>
);

export const KpcNotFound = () => {
  const { t } = useTranslationRef(appTranslationRef);

  const starElements = useMemo(
    () =>
      stars.map(s => (
        <div
          key={s.id}
          className={styles.star}
          style={{
            left: s.left,
            top: s.top,
            animationDelay: s.delay,
            animationDuration: s.duration,
            width: s.size,
            height: s.size,
          }}
        />
      )),
    [],
  );

  return (
    <div className={styles.container}>
      <div className={styles.stars}>{starElements}</div>
      <div className={styles.glow} />

      <div className={styles.illustration}>
        <div className={styles.astronaut}>
          <AstronautSvg />
        </div>
        <div className={styles.planet}>
          <div className={styles.planetRing} />
        </div>
      </div>

      <div className={styles.errorCode}>404</div>

      <div className={styles.textContent}>
        <Text variant="title-medium" weight="bold">
          {t('notFound.title')}
        </Text>
        <Text variant="body-medium" color="secondary">
          {t('notFound.description')}
        </Text>
      </div>

      <div className={styles.actions}>
        <ButtonLink href="/" variant="primary">
          {t('notFound.action')}
        </ButtonLink>
        <Button variant="tertiary" onPress={() => window.history.back()}>
          {t('notFound.goBack')}
        </Button>
      </div>
    </div>
  );
};
